import React, { useRef, useState, useEffect } from "react";
import DropdownMenu from "../../components/common/DropDownMenu/dropdownmenu";
import HeaderInboxDropdown from "./HeaderInboxDropdown";
import FeedbackModal from "../common/FeedbackModal";
import ChangeCredentialsModal from "../Pages/CredentialPolicy/ChangeCredentialsModal";
import CredentialExpiryWarningBanner from "../Pages/CredentialPolicy/CredentialExpiryWarningBanner";
import { needsCredentialExpiryWarning, mergeCredentialStatusIntoUser } from "../../utils/credentialGateUtils";
import {
  fetchStaffCredentialStatus,
  parseCredentialStatus,
} from "../../services/credentialPolicyService";
import { storeUserData } from "../../services/apiService";
import { submitFeedbackToClickUp } from "../../services/clickupService";
import useToast from "../../hooks/useToast";
import ToastContainer from "../common/ToastContainer";
import TimelineModal from "./TimelineModal";
import { useVisitTracking } from "../../context/VisitTrackingContext";
import { getDisplaySubtitle } from "../../data/pathLabels";
import { logout } from "../../services/apiService";
import { useNavigate } from "react-router-dom";
import { useUserData } from "../../hooks/useUserData";
import CtmsWorkspaceModal from "../common/ctms/CtmsWorkspaceModal";
import UserProfile from "../Pages/SuperAdmin/userProfile";
import { isSponsorCroSession } from "../../utils/sponsorCroAccess";
import "./Header.scss";
import "../Pages/SuperAdmin/userProfile.scss";

const Header = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showHelpDropdown, setShowHelpDropdown] = useState(false);
  const [showTimelineDropdown, setShowTimelineDropdown] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [myProfileModalOpen, setMyProfileModalOpen] = useState(false);
  const triggerRef = useRef(null);
  const helpTriggerRef = useRef(null);
  const timelineTriggerRef = useRef(null);
  const navigate = useNavigate();
  const { userData, refreshUserData } = useUserData();
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const { toasts, showToast, removeToast } = useToast();
  const { recentVisits } = useVisitTracking();
  const hasMultipleOrgs =
    Array.isArray(userData?.OtherOrgLink) &&
    userData.OtherOrgLink.length > 1;
  const isSponsorCro = isSponsorCroSession(userData);

  useEffect(() => {
    if (!userData?.StaffId || !userData?.OrganizationId) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await fetchStaffCredentialStatus(
          userData.StaffId,
          userData.OrganizationId,
        );
        const status = parseCredentialStatus(response);
        if (!status || cancelled) return;
        const merged = mergeCredentialStatusIntoUser(userData, status);
        storeUserData(merged);
        refreshUserData();
      } catch {
        // keep session data if refresh fails
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userData?.StaffId, userData?.OrganizationId]);

  const handleSwitchOrganization = () => {
    if (!hasMultipleOrgs) return;
    navigate("/organizations");
  };

  const menuItems = [
    {
      icon: "fas fa-user",
      label: "My Profile",
      onClick: () => handleRedirectToProfile(),
    },
    {
      icon: "fas fa-key",
      label: "Change login credentials",
      onClick: () => setShowCredentialsModal(true),
    },
    ...(hasMultipleOrgs
      ? [
          {
            icon: "fas fa-building",
            label: "Switch organization",
            onClick: () => handleSwitchOrganization(),
          },
        ]
      : []),
    { divider: true },
    {
      icon: "fas fa-sign-out-alt",
      label: "Logout",
      onClick: () => handleLogOut(),
    },
  ];
  const handleLogOut = () => {
    logout(navigate);
  };

  const handleRedirectToProfile = () => {
    const staffId = userData?.StaffId;
    if (!staffId) return;
    if (isSponsorCro) {
      setShowDropdown(false);
      setMyProfileModalOpen(true);
      return;
    }
    navigate(`/userprofile/${staffId}`);
  };

  const helpMenuItems = [
    {
      icon: "fas fa-book",
      label: "FAQ",
      onClick: () => navigate("/faq"),
    },
    {
      icon: "fas fa-ticket-alt",
      label: "My support tickets",
      onClick: () => navigate("/my-tickets"),
    },
    {
      icon: "fas fa-headset",
      label: "Contact support",
      onClick: () => setShowFeedbackModal(true),
    },
  ];

  const timelineMenuItems = [
    ...recentVisits.slice(0, 8).map((v) => {
      const displaySubtitle = getDisplaySubtitle(v.subtitle);
      return {
      icon: "fas fa-external-link-alt",
      label: displaySubtitle ? `${v.label} · ${displaySubtitle}` : v.label,
      onClick: () => {
        navigate(v.path);
        setShowTimelineDropdown(false);
      },
    };
    }),
    ...(recentVisits.length > 0 ? [{ divider: true }] : []),
    {
      icon: "fas fa-chart-line",
      label: "View Timeline",
      onClick: () => {
        setShowTimelineDropdown(false);
        setShowTimelineModal(true);
      },
    },
  ];

  const displayName = userData?.UserName || userData?.FirstName || "Profile";

  const handleFeedbackSubmit = async (formData) => {
    // Show submitting state in the modal button
    setIsSubmittingFeedback(true);

    try {
      const result = await submitFeedbackToClickUp(formData);

      if (result.success) {
        // ✅ Contact support request submitted
        showToast?.({
          title: "Support request sent",
          message:
            "Your message has been sent to our support team. We’ll get back to you shortly.",
          type: "success",
          icon: "fas fa-check-circle",
          duration: 5000,
        });

        // Close the modal immediately after successful submit
        setShowFeedbackModal(false);
      } else {
        // ❌ Something went wrong while sending contact request
        showToast?.({
          title: "Unable to send message",
          message:
            result.message ||
            result.error ||
            "We couldn't send your request right now. Please try again in a moment.",
          type: "danger",
          icon: "fas fa-exclamation-circle",
          duration: 7000,
        });
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      showToast?.({
        title: "Error",
        message:
          error.message ||
          "An unexpected error occurred while submitting feedback.",
        type: "danger",
        icon: "fas fa-exclamation-circle",
        duration: 7000,
      });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  return (
    <>
    <header className="app-header d-flex align-items-center justify-content-between py-2 shadow-sm">
      <div className="d-flex align-items-center">
        <img src="/logo.png" alt="Logo" className="logo me-3" />
       
      </div>

      <div className="header-actions">
        {/* Recent Visits dropdown */}
        <div className="header-action-dropdown">
          <button
            type="button"
            ref={timelineTriggerRef}
            className="header-action-btn"
            onClick={() => setShowTimelineDropdown(!showTimelineDropdown)}
            aria-expanded={showTimelineDropdown}
            aria-haspopup="true"
          >
            <i className="fas fa-history header-action-icon"></i>
            <span className="header-action-label">Recents</span>
            <i className="fas fa-chevron-down header-action-chevron" aria-hidden="true"></i>
          </button>
          <DropdownMenu
            items={timelineMenuItems}
            show={showTimelineDropdown}
            onClose={() => setShowTimelineDropdown(false)}
            trigger={timelineTriggerRef}
          />
        </div>

        {/* Inbox notifications — unread count + dropdown (max 5 unread) */}
        <HeaderInboxDropdown />

        {/* Help dropdown: FAQ + Contact support */}
        <div className="header-action-dropdown">
          <button
            className="header-action-btn"
            type="button"
            ref={helpTriggerRef}
            onClick={() => setShowHelpDropdown(!showHelpDropdown)}
            aria-expanded={showHelpDropdown}
            aria-haspopup="true"
          >
            <i className="far fa-question-circle header-action-icon"></i>
            <span className="header-action-label">Help</span>
            <i className="fas fa-chevron-down header-action-chevron" aria-hidden="true"></i>
          </button>
          <DropdownMenu
            items={helpMenuItems}
            show={showHelpDropdown}
            onClose={() => setShowHelpDropdown(false)}
            trigger={helpTriggerRef}
          />
        </div>

        {/* Profile dropdown */}
        <div className="header-action-dropdown">
          <button
            className="header-action-btn"
            type="button"
            ref={triggerRef}
            onClick={() => setShowDropdown(!showDropdown)}
            aria-expanded={showDropdown}
            aria-haspopup="true"
          >
            <i className="far fa-user-circle header-action-icon"></i>
            <span className="header-action-label">{displayName}</span>
            <i className="fas fa-chevron-down header-action-chevron" aria-hidden="true"></i>
          </button>
          <DropdownMenu
            items={menuItems}
            show={showDropdown}
            onClose={() => setShowDropdown(false)}
            trigger={triggerRef}
          />
        </div>
      </div>

    </header>
    {needsCredentialExpiryWarning(userData) && (
      <CredentialExpiryWarningBanner
        userData={userData}
        onChangeCredentials={() => setShowCredentialsModal(true)}
      />
    )}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        isSubmitting={isSubmittingFeedback}
      />
      <ChangeCredentialsModal
        open={showCredentialsModal}
        onClose={() => setShowCredentialsModal(false)}
      />
      {isSponsorCro && userData?.StaffId ? (
        <CtmsWorkspaceModal
          open={myProfileModalOpen}
          title="My Profile"
          titleId="sponsor-cro-my-profile-title"
          onClose={() => setMyProfileModalOpen(false)}
          className="cro-profile-workspace-modal"
          dialogClassName="ctms-workspace-modal__dialog--full"
          contentClassName="cro-profile-workspace-modal__content"
        >
          {myProfileModalOpen ? (
            <UserProfile
              key={String(userData.StaffId)}
              embedded
              embeddedStaffId={String(userData.StaffId)}
              onClose={() => setMyProfileModalOpen(false)}
            />
          ) : null}
        </CtmsWorkspaceModal>
      ) : null}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <TimelineModal
        isOpen={showTimelineModal}
        onClose={() => setShowTimelineModal(false)}
      />
    </>
  );
};

export default Header;
